import { DivergentPropertiesPerItemGroup } from '../store/items/itemsSlice'

interface LfcItemForLookup {
  configuratorContextId?: string
  dimensions: Array<{
    dynamakerCode?: string
    [key: string]: any
  }>
}

export const getHtmlMessageForDivergentProperties = (
  perItemGroup: Partial<DivergentPropertiesPerItemGroup>,
  content: Record<PropertyKey, any>,
  lfcItems: LfcItemForLookup[] = [],
  renderItems: Record<string, any[]> = {},
  languageCode = ''
): string => {
  const countryCode = languageCode?.slice(-2) ?? ''

  // Merge divergent values across item groups for the same property,
  // tracking which groups contribute to each property
  const divergentPropertiesMap = new Map<
    string,
    { origin: string; divergentValues: Set<string>; groups: Set<string> }
  >()
  for (const [group, divergentProperties] of Object.entries(perItemGroup)) {
    if (!divergentProperties) continue
    for (const [property, entry] of Object.entries(divergentProperties)) {
      const existing = divergentPropertiesMap.get(property)
      if (existing) {
        for (const v of Object.keys(entry.values)) existing.divergentValues.add(v)
        existing.groups.add(group)
      } else {
        divergentPropertiesMap.set(property, {
          origin: entry.origin,
          divergentValues: new Set(Object.keys(entry.values)),
          groups: new Set([group]),
        })
      }
    }
  }

  const divergentPropertiesArr = [...divergentPropertiesMap.entries()].map(
    ([property, { origin, divergentValues, groups }]) => ({
      property,
      origin,
      divergentValues: [...divergentValues],
      groups,
    })
  )

  if (!divergentPropertiesArr.length) {
    console.warn('No divergent properties in getHtmlMessageForDivergentProperties()')
    return ''
  }

  const resolveDisplayName = (
    property: string,
    origin: string,
    divergentValues: string[],
    groups: Set<string>
  ): string => {
    if (origin === 'dmProps') {
      // Scope lfcItems to only groups that have this property as divergent
      const relevantContextIds = new Set<string>()
      for (const group of groups) {
        for (const item of renderItems[group] ?? []) {
          const contextId = item?.props?.configData?.contextId
          if (contextId) relevantContextIds.add(contextId)
        }
      }
      const scopedLfcItems = relevantContextIds.size
        ? lfcItems.filter((lfc) => lfc.configuratorContextId && relevantContextIds.has(lfc.configuratorContextId))
        : lfcItems

      const names = new Set<string>()
      for (const lfcItem of scopedLfcItems) {
        for (const dim of lfcItem.dimensions ?? []) {
          if (dim.dynamakerCode !== property) continue
          const hasDivergentValue = dim.values?.some((v: any) => divergentValues.includes(String(v.dynamakerValue)))
          if (!hasDivergentValue) continue
          const name = dim[`dimensionName${countryCode}`]?.trim()
          if (name) names.add(name)
        }
      }
      if (names.size) return [...names].join(' / ')
    }
    return (content?.filters?.[property]?.title ?? property).trim()
  }

  const displayNames = divergentPropertiesArr.map(({ property, origin, divergentValues, groups }) =>
    resolveDisplayName(property, origin, divergentValues, groups)
  )
  const uniqueDisplayNames = [...new Set(displayNames)]

  const unorderedListHtml = `<ul><li>${uniqueDisplayNames.join('</li><li>')}</li></ul>`

  return `<p>${content?.other?.divergentPropertiesDescription}</p>${unorderedListHtml}`
}
